import { Fragment, useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import { MessageCircle, Reply, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const REPLY_NAME_KEY = 'admin_reply_name';

function getReplyName() {
  try {
    return localStorage.getItem(REPLY_NAME_KEY) || 'Roy';
  } catch {
    return 'Roy';
  }
}

// The server stores times in UTC without a zone marker; without one,
// the browser would read them as local time.
function asUtc(value) {
  if (!value) return null;
  const text = String(value);
  return new Date(/(Z|[+-]\d\d:?\d\d)$/i.test(text) ? text : `${text}Z`);
}

function AdminComments({ onLogout }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  // Comments newer than the last visit get a "New" tag on this visit.
  const [previousSeenAt, setPreviousSeenAt] = useState(undefined);
  const [replyingToId, setReplyingToId] = useState(null);
  const [replyName, setReplyName] = useState(getReplyName);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);

  useEffect(() => {
    fetchComments();
  }, []);

  const fetchComments = async () => {
    try {
      const response = await axios.get('/admin/comments');
      setComments(response.data);
      axios.post('/admin/comments/mark-seen')
        .then((seen) => setPreviousSeenAt(seen.data.previous_seen_at || null))
        .catch(() => {});
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast.error('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (comment) => {
    if (!window.confirm(`Delete this comment from ${comment.name}?`)) return;
    setDeletingId(comment.id);
    try {
      await axios.delete(`/admin/comments/${comment.id}`);
      setComments((prev) => prev.filter((c) => c.id !== comment.id));
      toast.success('Comment deleted');
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    } finally {
      setDeletingId(null);
    }
  };

  const handleReply = async (comment) => {
    if (!replyName.trim() || !replyText.trim()) {
      toast.error('Enter your name and a reply');
      return;
    }
    setReplySending(true);
    try {
      const response = await axios.post(`/admin/comments/${comment.id}/reply`, {
        name: replyName.trim(),
        text: replyText.trim()
      });
      try { localStorage.setItem(REPLY_NAME_KEY, replyName.trim()); } catch { /* not remembering is fine */ }
      const reply = {
        ...response.data,
        post_title: comment.post_title,
        post_slug: comment.post_slug,
        reply_to_name: comment.name
      };
      setComments((prev) => [reply, ...prev]);
      setReplyingToId(null);
      setReplyText('');
      toast.success(comment.email && comment.notify_replies !== false ? `Reply posted — ${comment.name} will get an email` : 'Reply posted');
    } catch (error) {
      console.error('Error posting reply:', error);
      toast.error(error.response?.data?.detail || 'Failed to post reply');
    } finally {
      setReplySending(false);
    }
  };

  const isNew = (comment) => {
    if (comment.is_author) return false;
    if (previousSeenAt === undefined) return false;
    if (previousSeenAt === null) return true;
    return asUtc(comment.created_at) > asUtc(previousSeenAt);
  };
  const newCount = comments.filter(isNew).length;

  const formatDate = (value) => {
    if (!value) return '-';
    const date = asUtc(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
  };

  return (
    <AdminLayout onLogout={onLogout} currentPage="comments">
      <div className="page-header">
        <h1>Comments</h1>
        {newCount > 0 && (
          <p style={{ margin: '6px 0 0', color: '#666' }} data-testid="new-comments-summary">
            {newCount} new since your last visit
          </p>
        )}
      </div>

      {loading ? (
        <div className="loading-screen">
          <div className="loading-spinner"></div>
        </div>
      ) : comments.length === 0 ? (
        <div className="empty-state">
          <MessageCircle size={48} strokeWidth={1} />
          <h3>No comments yet</h3>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Post</th>
                <th>Name</th>
                <th>Comment</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {comments.map((comment) => (
                <Fragment key={comment.id}>
                <tr className={isNew(comment) ? 'comment-row-new' : ''}>
                  <td>
                    {comment.post_slug ? (
                      <a href={`/post/${comment.post_slug}`} target="_blank" rel="noopener noreferrer">
                        {comment.post_title}
                      </a>
                    ) : (
                      comment.post_title
                    )}
                  </td>
                  <td>
                    <div>
                      {comment.name}
                      {comment.is_author && (
                        <span style={{ marginLeft: '6px', padding: '1px 7px', borderRadius: '10px', background: '#1a1a1a', color: '#fff', fontSize: '11px' }}>
                          Author
                        </span>
                      )}
                      {isNew(comment) && <span className="new-comment-tag">New</span>}
                    </div>
                    {comment.reply_to_name && (
                      <div style={{ fontSize: '12px', color: '#888' }}>reply to {comment.reply_to_name}</div>
                    )}
                    {comment.email && (
                      <div style={{ fontSize: '12px', color: '#888' }}>{comment.email}</div>
                    )}
                  </td>
                  <td style={{ maxWidth: '420px', whiteSpace: 'pre-wrap' }}>{comment.text}</td>
                  <td>{formatDate(comment.created_at)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          setReplyingToId(replyingToId === comment.id ? null : comment.id);
                          setReplyText('');
                        }}
                        title="Reply to this comment"
                      >
                        <Reply size={16} /> Reply
                      </button>
                      <button
                        className="btn btn-secondary"
                        disabled={deletingId === comment.id}
                        onClick={() => handleDelete(comment)}
                        title="Delete Comment"
                      >
                        <Trash2 size={16} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
                {replyingToId === comment.id && (
                  <tr>
                    <td colSpan={5} style={{ background: '#fafafa' }}>
                      <div style={{ maxWidth: '640px' }}>
                        <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                          Replying to {comment.name}
                          {comment.email && comment.notify_replies !== false
                            ? ` — they'll get an email with your reply`
                            : ' — no email on file, so they’ll only see it on the post'}
                        </div>
                        <input
                          type="text"
                          value={replyName}
                          onChange={(e) => setReplyName(e.target.value)}
                          placeholder="Your name as shown"
                          style={{ width: '220px', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: '4px', fontSize: '14px', marginBottom: '8px' }}
                        />
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder={`Reply to ${comment.name}...`}
                          rows={4}
                          autoFocus
                          style={{ display: 'block', width: '100%', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical', marginBottom: '8px' }}
                        />
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button className="btn btn-primary" disabled={replySending} onClick={() => handleReply(comment)}>
                            {replySending ? 'Posting…' : 'Post Reply'}
                          </button>
                          <button className="btn btn-secondary" onClick={() => setReplyingToId(null)}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminComments;
